import { Box } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { Error } from "@ui/error/Error";
import Spacer from "@ui/spacer/Spacer";
import { useState } from "react";
import { GD } from "src/GD";
import { FormParser } from "src/view/FormParser";
import { StartScreenView } from "../StartScreenView";
import {
	AUTH_FORM_ACTIONS_SX,
	AUTH_PRIMARY_BUTTON_SX,
	AUTH_SECONDARY_BUTTON_SX,
	AUTH_TWO_COLUMN_SX,
	AuthCard,
	AuthDivider,
	AuthStatus,
} from "./AuthLayout";

const form = {
	name: "",
	$__name_label: "Full name",
	$__name_autoComplete: "name",
	phone: "",
	$__phone_label: "Phone number",
	$__phone_autoComplete: "tel",
	password: "",
	$__password_type: "password",
	$__password_autoComplete: "new-password",
	confirm_pssword: "",
	$__confirm_pssword_type: "password",
	$__confirm_pssword_autoComplete: "new-password",
	$__confirm_pssword_label: "Confirm password",
	$__decorator: {
		blocks: [
			{ order: ["name", "phone"], blockless: true, sx: AUTH_TWO_COLUMN_SX },
			{ order: ["password", "confirm_pssword"], blockless: true, sx: AUTH_TWO_COLUMN_SX },
		],
	},
};

type TNewUserForm = typeof form;

export const InvitePage = ({ code }: { code: string }) => {
	const [busy, setBusy] = useState(false);
	const [cancelled, setCancelled] = useState(false);
	const [error, setError] = useState<IError>();
	const [invite] = GD.REQ_INVITE_GET.useRequest(undefined, { code });

	const getForm = () => ({ ...form });

	const validate = (data: TNewUserForm): IError | undefined => {
		if (!data.name) return { code: 0, message: "Empty name" };
		if (!data.phone) return { code: 0, message: "Empty phone" };
		if (!data.password) return { code: 0, message: "Empty password" };
		if (data.password !== data.confirm_pssword) return { code: 0, message: "The passwords don't match" };
	};

	const accept = async (userForm?: TNewUserForm) => {
		setError(undefined);
		if (userForm) {
			const invalid = validate(userForm);
			if (invalid) return { error: invalid };
		}
		return GD.REQ_API.request({
			method: "users/invite/accept",
			data: {
				token: code,
				user: userForm ? { name: userForm.name, phone: userForm.phone, password: userForm.password } : null,
			},
		});
	};

	const callAccept = async () => {
		setBusy(true);
		const res = await accept();
		setBusy(false);
		if (res.error) setError(res.error);
		else location.href = "/";
	};

	const cancel = async () => {
		setError(undefined);
		setBusy(true);
		const req = await GD.REQ_API.request({
			method: "users/invite/cancel",
			data: { token: code },
		});
		setBusy(false);
		if (req.error) setError(req.error);
		else setCancelled(true);
	};

	if (!invite) {
		return (
			<StartScreenView busy>
				<AuthCard icon="loading" size="wide" eyebrow="Workspace invitation" title="Loading your invitation" subtitle="We are retrieving the workspace and access details.">
					<AuthStatus title="Checking invitation">Please wait a moment.</AuthStatus>
				</AuthCard>
			</StartScreenView>
		);
	}

	if (invite.error) {
		return (
			<StartScreenView>
				<AuthCard icon="error" eyebrow="Workspace invitation" title="Invitation unavailable" subtitle="This invitation cannot be opened in its current state.">
					<Error error={invite.error} />
					<AuthDivider />
					<Button title="Back to sign in" variant="secondary" onClick={() => (location.href = "/")} sx={AUTH_SECONDARY_BUTTON_SX} />
				</AuthCard>
			</StartScreenView>
		);
	}

	if (cancelled) {
		return (
			<StartScreenView>
				<AuthCard icon="success" eyebrow="Workspace invitation" title="Invitation declined" subtitle="No workspace access was added to your account.">
					<AuthStatus title="Invitation closed">You can safely close this page or return to sign in.</AuthStatus>
					<Spacer size="22px" />
					<Button title="Back to sign in" variant="primary" onClick={() => (location.href = "/")} sx={AUTH_PRIMARY_BUTTON_SX} />
				</AuthCard>
			</StartScreenView>
		);
	}

	return (
		<StartScreenView busy={busy}>
			<AuthCard icon="mail" size="wide" eyebrow="Workspace invitation" title="Join Cardpartner" subtitle="Review the invitation and confirm whether you want to join this workspace.">
				<AuthStatus title="Access request">
					You have been invited to join the Cardpartner project.
				</AuthStatus>
				{error ? (
					<>
						<Spacer size="16px" />
						<Error error={error} />
					</>
				) : null}

				{!invite.data.opened_at ? (
					<>
						<Spacer size="22px" />
						<FormParser
							key="inviteForm"
							fullSizeButtons={true}
							actionsSx={AUTH_FORM_ACTIONS_SX}
							ignoreObserver={true}
							secondaryButton={{ title: "Decline invitation", variant: "secondary", sx: { ...AUTH_SECONDARY_BUTTON_SX, flex: 1 }, onClick: cancel }}
							primaryButton={{ title: "Accept invitation", variant: "primary", sx: { ...AUTH_PRIMARY_BUTTON_SX, flex: 1 } }}
							onSubmit={async (data: TNewUserForm): Promise<any> => {
								const res = await accept(data);
								if (res.error) setError(res.error);
								else location.href = "/";
								return res;
							}}
							onRequest={async () => getForm()}
						/>
					</>
				) : null}

				{invite.data.status === "used" ? (
					<>
						<Spacer size="22px" />
						<Box sx={AUTH_TWO_COLUMN_SX}>
							<Button title="Decline invitation" variant="secondary" onClick={cancel} sx={AUTH_SECONDARY_BUTTON_SX} />
							<Button title="Accept invitation" variant="primary" onClick={callAccept} sx={AUTH_PRIMARY_BUTTON_SX} />
						</Box>
					</>
				) : null}

				<AuthDivider />
				<Button title="Back to sign in" variant="secondary" onClick={() => (location.href = "/")} sx={AUTH_SECONDARY_BUTTON_SX} />
			</AuthCard>
		</StartScreenView>
	);
};
